import type { QueryClient } from "@tanstack/react-query";
import {
  GenericError,
  MissingRefreshTokenError,
  OAuthError,
  type LogoutOptions,
} from "@auth0/auth0-react";
import { getTenantSlugFromPathname } from "~/lib/tenant-routing";
import { clearLoginStatusCookie } from "~/lib/login-status-cookie";

type LogoutFn = (options?: LogoutOptions) => Promise<void>;

let activeRecoveryPromise: Promise<void> | null = null;
const POST_LOGOUT_REDIRECT_COOKIE = "ph_post_logout_redirect";
const POST_LOGOUT_FLAG_KEY = "ph_just_logged_out";

// Module-level flag to prevent PostLogoutRedirector from racing with
// the Auth0 /v2/logout navigation. When logoutWithSafeReturnTo's openUrl
// handler fires, it sets this to true. PostLogoutRedirector must check
// this and skip its redirect if a logout navigation is already underway.
let logoutNavigationInProgress = false;

export function isLogoutNavigationInProgress() {
  return logoutNavigationInProgress;
}

export function getContextualLoginPath(pathname = window.location.pathname) {
  const tenantSlug = getTenantSlugFromPathname(pathname);
  return tenantSlug ? `/${tenantSlug}/login` : "/login";
}

export function getAllowedLogoutReturnTo() {
  return (
    import.meta.env.VITE_LOGOUT_URL ??
    import.meta.env.VITE_PUBLIC_URL ??
    window.location.origin
  );
}

export function normalizePostLogoutPath(returnTo?: string) {
  if (!returnTo) {
    return getContextualLoginPath();
  }

  if (returnTo.startsWith("/")) {
    return returnTo;
  }

  try {
    const parsedUrl = new URL(returnTo);
    if (parsedUrl.origin === window.location.origin) {
      return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
    }
  } catch {
    return getContextualLoginPath();
  }

  return getContextualLoginPath();
}

function matchesRecoverableAuthMessage(value?: string) {
  if (!value) return false;

  const normalizedValue = value.toLowerCase();
  return (
    normalizedValue.includes("access_denied") ||
    normalizedValue.includes("invalid_grant") ||
    normalizedValue.includes("missing_refresh_token") ||
    normalizedValue.includes("refresh token") ||
    normalizedValue.includes("403") ||
    normalizedValue.includes("unauthorized")
  );
}

export function isRecoverableAuthError(error: unknown) {
  if (error instanceof MissingRefreshTokenError) {
    return true;
  }

  // Only match Auth0-specific error types to avoid false positives.
  // Generic Error instances (e.g. backend ForbiddenError with "unauthorized"
  // in the message) should NOT trigger session recovery and force-logout.
  if (error instanceof GenericError || error instanceof OAuthError) {
    return (
      matchesRecoverableAuthMessage(error.error) ||
      matchesRecoverableAuthMessage(error.error_description) ||
      matchesRecoverableAuthMessage(error.message)
    );
  }

  return false;
}

export async function clearBrowserAuthData(queryClient?: QueryClient) {
  clearLoginStatusCookie();

  try {
    queryClient?.clear();
  } catch (error) {
    console.warn("[Auth] Failed to clear query cache:", error);
  }

  try {
    window.localStorage.clear();
  } catch (error) {
    console.warn("[Auth] Failed to clear localStorage:", error);
  }

  try {
    // Preserve the just-logged-out flag across sessionStorage clear.
    // This flag is set by logoutWithSafeReturnTo BEFORE clearBrowserAuthData
    // runs, and login pages need it to avoid auto-redirecting back to Auth0.
    const preserveLogoutFlag = window.sessionStorage.getItem(POST_LOGOUT_FLAG_KEY);
    window.sessionStorage.clear();
    if (preserveLogoutFlag) {
      window.sessionStorage.setItem(POST_LOGOUT_FLAG_KEY, preserveLogoutFlag);
    }
  } catch (error) {
    console.warn("[Auth] Failed to clear sessionStorage:", error);
  }

  if ("caches" in window) {
    try {
      const cacheKeys = await window.caches.keys();
      await Promise.allSettled(
        cacheKeys.map((cacheKey) => window.caches.delete(cacheKey))
      );
    } catch (error) {
      console.warn("[Auth] Failed to clear Cache Storage:", error);
    }
  }

  const indexedDbWithDatabases = window.indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };

  if (typeof indexedDbWithDatabases.databases === "function") {
    try {
      const databases = await indexedDbWithDatabases.databases();
      await Promise.allSettled(
        databases
          .map((database) => database.name)
          .filter((name): name is string => !!name)
          .map(
            (name) =>
              new Promise<void>((resolve) => {
                const request = window.indexedDB.deleteDatabase(name);
                request.onsuccess = () => resolve();
                request.onerror = () => resolve();
                request.onblocked = () => resolve();
              })
          )
      );
    } catch (error) {
      console.warn("[Auth] Failed to clear IndexedDB:", error);
    }
  }
}

function setPostLogoutRedirect(path: string) {
  document.cookie = `${POST_LOGOUT_REDIRECT_COOKIE}=${encodeURIComponent(
    path
  )}; Path=/; Max-Age=120; SameSite=Lax`;
}

function clearPostLogoutRedirectCookie() {
  document.cookie = `${POST_LOGOUT_REDIRECT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function hasPendingPostLogoutRedirect(): boolean {
  return document.cookie
    .split("; ")
    .some((entry) => entry.startsWith(`${POST_LOGOUT_REDIRECT_COOKIE}=`));
}

export function consumePostLogoutRedirect() {
  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${POST_LOGOUT_REDIRECT_COOKIE}=`));

  if (!cookie) {
    return null;
  }

  clearPostLogoutRedirectCookie();
  return decodeURIComponent(cookie.split("=").slice(1).join("="));
}

/**
 * Returns true (and clears the flag) if the user just completed a logout.
 * Login/signup pages should check this and skip auto-redirecting to Auth0.
 */
export function consumeJustLoggedOutFlag(): boolean {
  try {
    if (sessionStorage.getItem(POST_LOGOUT_FLAG_KEY)) {
      sessionStorage.removeItem(POST_LOGOUT_FLAG_KEY);
      return true;
    }
  } catch {
    // sessionStorage may be unavailable
  }
  return false;
}

export async function logoutWithSafeReturnTo({
  logout,
  postLogoutPath,
  queryClient,
  clearSiteData = false,
}: {
  logout: LogoutFn;
  postLogoutPath?: string;
  queryClient?: QueryClient;
  clearSiteData?: boolean;
}) {
  const normalizedPostLogoutPath = postLogoutPath
    ? normalizePostLogoutPath(postLogoutPath)
    : null;
  const auth0ReturnTo = normalizedPostLogoutPath
    ? window.location.origin
    : getAllowedLogoutReturnTo();

  if (normalizedPostLogoutPath) {
    setPostLogoutRedirect(normalizedPostLogoutPath);
  } else {
    clearPostLogoutRedirectCookie();
  }

  // Always mark intentional logouts so login/signup pages don't
  // auto-redirect back to Auth0 before the user can see the
  // "You have been logged out" screen.
  try {
    sessionStorage.setItem(POST_LOGOUT_FLAG_KEY, "1");
  } catch {
    // sessionStorage may be unavailable
  }

  if (clearSiteData) {
    await clearBrowserAuthData(queryClient);
  }

  // Mark that a logout navigation is about to happen. This prevents
  // PostLogoutRedirector from racing with our window.location.replace
  // call below. Without this, clearing localStorage causes Auth0 SDK
  // to report isAuthenticated=false, which triggers PostLogoutRedirector
  // to call window.location.replace("/login") — overriding our
  // window.location.replace(auth0_logout_url) and preventing the
  // Auth0 session cookie from ever being cleared.
  logoutNavigationInProgress = true;

  try {
    await logout({
      logoutParams: {
        returnTo: auth0ReturnTo,
      },
      openUrl: (url) => {
        // Clear Auth0 cached tokens from localStorage inside openUrl —
        // AFTER the SDK has finished preparing the logout URL but BEFORE
        // we navigate away.
        clearLoginStatusCookie();
        try {
          queryClient?.clear();
          window.localStorage.clear();
        } catch {
          // localStorage may be unavailable
        }
        window.location.replace(url);
      },
    });
  } catch (error) {
    console.error("[Auth] Failed to complete Auth0 logout:", error);
    // Even on failure, clear localStorage to prevent stale tokens
    clearLoginStatusCookie();
    try {
      window.localStorage.clear();
    } catch {
      // localStorage may be unavailable
    }
    window.location.replace(normalizedPostLogoutPath ?? getAllowedLogoutReturnTo());
  }
}

export async function recoverFromBrokenAuthSession({
  logout,
  queryClient,
}: {
  logout: LogoutFn;
  queryClient?: QueryClient;
}) {
  if (activeRecoveryPromise) {
    return activeRecoveryPromise;
  }

  activeRecoveryPromise = (async () => {
    const loginPath = getContextualLoginPath();
    try {
      await logoutWithSafeReturnTo({
        logout,
        postLogoutPath: loginPath,
        queryClient,
        clearSiteData: true,
      });
    } finally {
      activeRecoveryPromise = null;
    }
  })();

  return activeRecoveryPromise;
}
